<template>
  <div class="appeal-distribute">
    <!-- 顶部导航 -->
    <div class="page-header">
      <div class="left">
        <i class="el-icon-back back-icon" @click="$router.back()"></i>
        <i class="el-icon-s-claim header-icon"></i>
        <span class="title">申诉下发配置</span>
      </div>
      <div class="right">
        <el-button @click="$router.back()">取消</el-button>
        <el-button type="primary" @click="handleDispatch" :loading="dispatching">确定下发</el-button>
      </div>
    </div>

    <!-- 基础信息配置 -->
    <div class="info-row">
      <div class="info-field">
        <label>批次名称</label>
        <el-input v-model="form.batchName" placeholder="输入批次名称"></el-input>
      </div>
      <div class="info-field">
        <label>申诉时间范围</label>
        <el-date-picker
          v-model="timeRange"
          type="daterange"
          range-separator="至"
          start-placeholder="开始时间"
          end-placeholder="结束时间"
          value-format="yyyy-MM-dd HH:mm:ss"
          :default-time="['00:00:00', '23:59:59']"
          prefix-icon="el-icon-date">
        </el-date-picker>
      </div>
      <div class="info-field">
        <label>申诉月份</label>
        <span style="font-size: 14px; color: #303133;">{{ form.appealMonth || '-' }}</span>
      </div>
      <div class="info-field">
        <label>备注</label>
        <el-input v-model="form.remark" placeholder="请输入备注信息 (可选)"></el-input>
      </div>
    </div>

    <!-- 主体工作区 -->
    <div class="workspace">
      <!-- 左侧：门店与题目 -->
      <div class="panel left-panel">
        <div class="panel-header">
          <div class="left">
            <i class="el-icon-office-building" style="color: #3b82f6;"></i>
            <span class="title">门店与问题清单</span>
            <el-tag size="mini" type="success" effect="plain">{{ totalItems }} 个待处理</el-tag>
          </div>
          <div class="right">
            <el-tooltip v-if="!waveId" content="当前入口未传 waveId，无法引入阶段证据" placement="top">
              <span>
                <el-button size="mini" type="primary" plain icon="el-icon-folder-checked" disabled>
                  从其它阶段引入证据
                </el-button>
              </span>
            </el-tooltip>
            <el-button v-else size="mini" type="primary" plain icon="el-icon-folder-checked" @click="openStageImport">
              从其它阶段引入证据
            </el-button>
          </div>
        </div>
        <div class="panel-body">
          <!-- 按店折叠展示 -->
          <div class="dealer-group" v-for="dealer in treeData" :key="dealer.id">
            <div class="dealer-group-header" @click="toggleDealer(dealer.id)">
              <div class="dealer-group-left">
                <i :class="isDealerExpanded(dealer.id) ? 'el-icon-arrow-down' : 'el-icon-arrow-right'" class="expand-arrow"></i>
                <i class="el-icon-office-building dealer-icon"></i>
                <span class="dealer-group-name">{{ dealer.label }}</span>
              </div>
              <el-tag size="mini" effect="plain">{{ dealer.children.length }} 题</el-tag>
            </div>
            <div class="dealer-group-body" v-show="isDealerExpanded(dealer.id)">
              <!-- 门店级证据（从阶段 1/2/4 引入，挂门店不挂指标） -->
              <div
                v-if="dealerLevelEvidences[dealer.dealerCode] && dealerLevelEvidences[dealer.dealerCode].length > 0"
                class="dealer-level-evidence">
                <div class="dealer-level-evidence-header">
                  <i class="el-icon-paperclip"></i>
                  <span>门店级证据（{{ dealerLevelEvidences[dealer.dealerCode].length }} 个文件）</span>
                </div>
                <div class="dealer-level-evidence-list">
                  <div
                    v-for="(ev, idx) in dealerLevelEvidences[dealer.dealerCode]"
                    :key="ev.url"
                    class="dealer-level-evidence-item">
                    <i class="dealer-level-evidence-icon"
                       :class="dealerEvIconClass(ev.fileType)"
                       @click="openDealerEvidence(ev)"
                       :style="{ cursor: 'pointer', color: dealerEvIconColor(ev.fileType) }"></i>
                    <el-tooltip :content="ev.displayName" placement="top" :open-delay="500">
                      <span class="dealer-level-evidence-name" @click="openDealerEvidence(ev)">{{ ev.displayName }}</span>
                    </el-tooltip>
                    <i class="el-icon-close dealer-level-evidence-remove"
                       @click.stop="removeDealerLevelEvidence(dealer.dealerCode, idx)"
                       title="从本批次移除"></i>
                  </div>
                </div>
              </div>

              <!-- 题目列表 -->
              <div
                v-for="item in dealer.children"
                :key="item.id"
                class="question-card"
                @dragover.prevent="handleDragOver($event, item)"
                @dragenter.prevent="handleDragEnter($event, item)"
                @dragleave="handleDragLeave($event, item)"
                @drop="handleDropOnNode($event, item)"
                :class="{ 'drop-active': activeDropNodeId === item.id }">
                <div class="question-header">
                  <span class="question-label" v-html="item.label"></span>
                </div>
                <div v-if="item.reviewRemark" class="review-remark-hint">
                  <i class="el-icon-chat-line-square"></i> 审核备注：{{ item.reviewRemark }}
                </div>
                
                <!-- 已分配文件 -->
                <div v-if="assignedMap[item.assignmentKey] && assignedMap[item.assignmentKey].length > 0" class="assigned-files">
                  <div v-for="(file, index) in assignedMap[item.assignmentKey]" :key="index" class="file-thumb">
                    <el-image 
                      v-if="file.fileType === 'image'" 
                      :src="file.src" 
                      :preview-src-list="[file.src]"
                      class="img">
                    </el-image>
                    <div v-else-if="file.fileType === 'video'" class="file-icon" @click.stop="openMediaPreview(file)" style="cursor: pointer;">
                      <i class="el-icon-video-camera" style="color: #f59e0b;"></i>
                    </div>
                    <div v-else-if="file.fileType === 'audio'" class="file-icon" @click.stop="openMediaPreview(file)" style="cursor: pointer;">
                      <i class="el-icon-headset" style="color: #22c55e;"></i>
                    </div>
                    <div v-else class="file-icon" @click.stop="openDocument(file)" style="cursor: pointer;">
                      <i class="el-icon-document" style="color: #3b82f6;"></i>
                    </div>
                    <div class="remove-btn" @click.stop="removeAssignment(item.assignmentKey, index)">
                      <i class="el-icon-close"></i>
                    </div>
                    <el-tooltip :content="file.name" placement="top" :open-delay="500">
                      <div class="file-name">{{ file.name }}</div>
                    </el-tooltip>
                  </div>
                </div>
                
                <!-- 空拖拽区域 -->
                <div v-else class="drop-zone" :class="{ 'drop-zone-active': activeDropNodeId === item.id }">
                  <i class="el-icon-circle-plus-outline"></i>
                  <span>拖拽证据文件到此处</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- 右侧：证据池 -->
      <div class="panel right-panel">
        <div class="panel-header">
          <div class="left">
            <i class="el-icon-files" style="color: #3b82f6;"></i>
            <span class="title">证据文件池</span>
          </div>
          <div class="right">
            <span class="toggle-label">仅显示未分配</span>
            <el-switch v-model="showAllFiles" active-color="#3b82f6"></el-switch>
          </div>
        </div>
        <div class="panel-toolbar">
          <el-button size="small" type="primary" round @click="smartMatch">
            <i class="el-icon-connection"></i> 智能匹配
          </el-button>
          <el-upload action="" :http-request="uploadFile" :show-file-list="false" multiple style="display: inline-block;">
            <el-button size="small" round plain><i class="el-icon-upload2"></i> 上传文件</el-button>
          </el-upload>
          <el-button size="small" round plain @click="triggerFolderUpload">
            <i class="el-icon-folder-opened"></i> 上传文件夹
          </el-button>
          <input type="file" ref="folderInput" webkitdirectory style="display: none" @change="handleFolderSelect" />
        </div>
        
        <!-- 拖入上传覆盖层 -->
        <div 
          class="panel-body" 
          v-loading="uploading"
          element-loading-text="正在上传中..."
          @dragover.prevent="handlePoolDragOver"
          @dragleave="handlePoolDragLeave"
          @drop.prevent="handlePoolDrop">
          
          <div v-if="visibleFiles.length === 0" class="empty-pool">
            <div class="empty-icon">
              <i class="el-icon-upload"></i>
            </div>
            <h4>准备上传您的证据</h4>
            <p>拖拽文件或文件夹到此处，或者点击上<br>方按钮上传</p>
            <div class="file-types">
              <span><i class="el-icon-picture-outline"></i> JPG / PNG</span>
              <span><i class="el-icon-document"></i> PDF Document</span>
              <span><i class="el-icon-video-camera"></i> 视频文件</span>
              <span><i class="el-icon-headset"></i> 音频文件</span>
              <span><i class="el-icon-document-copy"></i> Excel / Word</span>
            </div>
          </div>

          <div class="file-grid">
            <div 
              v-for="file in visibleFiles" 
              :key="file.uid" 
              class="pool-file-item"
              draggable="true"
              @dragstart="handleDragStart($event, file)">
              <div class="file-content">
                <el-image 
                  v-if="file.fileType === 'image'" 
                  :src="file.src" 
                  fit="cover"
                  class="file-img">
                </el-image>
                <div v-else-if="file.fileType === 'video'" class="file-icon-large file-video" @click="openMediaPreview(file)" style="cursor: pointer;">
                  <i class="el-icon-video-camera"></i>
                </div>
                <div v-else-if="file.fileType === 'audio'" class="file-icon-large file-audio" @click="openMediaPreview(file)" style="cursor: pointer;">
                  <i class="el-icon-headset"></i>
                </div>
                <div v-else class="file-icon-large file-document" @click="openDocument(file)" style="cursor: pointer;">
                  <i class="el-icon-document"></i>
                </div>
                <div class="status-badge" v-if="file.isAssigned">
                  <i class="el-icon-check"></i>
                </div>
                <div class="pool-remove-btn" @click.stop="removeFromPool(file)" title="移除">
                  <i class="el-icon-close"></i>
                </div>
              </div>
              <div class="file-name" :title="file.name">{{ file.name }}</div>
            </div>
          </div>
          
          <!-- 拖入遮罩 -->
          <div v-if="isPoolDropping" class="drop-mask">
            <i class="el-icon-upload"></i>
            <p>释放以上传文件到证据池</p>
          </div>
        </div>
        <!-- 底部提示栏 -->
        <div class="panel-footer">
          <span>支持批量拖拽多级文件夹</span>
          <span>文件上限: 500MB</span>
        </div>
      </div>
    </div>

    <!-- 匹配结果弹窗 -->
    <el-dialog title="智能匹配结果" :visible.sync="showMatchResult" width="600px">
      <p>精确匹配：<span style="color: #67C23A">{{ matchResult.success }}</span> 个文件</p>
      <p>店铺级别匹配：<span style="color: #409EFF">{{ matchResult.storeLevel || 0 }}</span> 个文件</p>
      <p>未匹配：<span style="color: #F56C6C">{{ matchResult.failed }}</span> 个文件</p>
      <el-table :data="matchResult.list" height="300" border size="mini">
        <el-table-column prop="name" label="文件名" show-overflow-tooltip></el-table-column>
        <el-table-column prop="status" label="状态" width="100">
          <template slot-scope="{row}">
             <el-tag v-if="row.status === 'success'" type="success">精确匹配</el-tag>
             <el-tag v-else-if="row.status === 'store'" type="">店铺匹配</el-tag>
             <el-tag v-else type="info">未匹配</el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="target" label="匹配目标" show-overflow-tooltip></el-table-column>
      </el-table>
      <span slot="footer">
        <el-button type="primary" @click="showMatchResult = false">确 定</el-button>
      </span>
    </el-dialog>

    <!-- 媒体预览弹窗 -->
    <el-dialog 
      :title="previewFile ? previewFile.name : '媒体预览'" 
      :visible.sync="showMediaPreview" 
      width="600px"
      :before-close="closeMediaPreview"
      append-to-body>
      <div class="media-preview-container">
        <video 
          v-if="previewFile && previewFile.fileType === 'video'" 
          :src="previewFile.src" 
          controls 
          class="preview-video"
          ref="previewVideo">
        </video>
        <audio
          v-else-if="previewFile && previewFile.fileType === 'audio'"
          :src="previewFile.src"
          controls
          class="preview-audio"
          ref="previewAudio">
        </audio>
      </div>
    </el-dialog>

    <!-- 阶段证据引入弹窗 -->
    <stage-evidence-import-dialog
      :visible.sync="stageImportVisible"
      :wave-id="waveId"
      :appeal-form-key="appealFormKey"
      :dealer-codes="appealDealerCodes"
      @imported="handleStageImported" />
  </div>
</template>

<script>
// CHANGE: Import from new API file
import * as specialApi from 'index/api/project/appeal'
import axios from 'axios'
import { smartUpload } from '@/utils/ossUpload'
// import * as utils from '@/utils'
import { getResourceSrc } from '@/utils'
import StageEvidenceImportDialog from './StageEvidenceImportDialog.vue'

// Helper functions
const uuid = () => {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    var r = Math.random() * 16 | 0, v = c == 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
}

const formatDate = (date, fmt) => {
  if (!date) return ''
  date = new Date(date)
  var o = {
    'M+': date.getMonth() + 1,
    'd+': date.getDate(),
    'H+': date.getHours(),
    'm+': date.getMinutes(),
    's+': date.getSeconds(),
    'q+': Math.floor((date.getMonth() + 3) / 3),
    'S': date.getMilliseconds()
  }
  if (/(y+)/.test(fmt)) fmt = fmt.replace(RegExp.$1, (date.getFullYear() + '').substr(4 - RegExp.$1.length))
  for (var k in o) {
    if (new RegExp('(' + k + ')').test(fmt)) fmt = fmt.replace(RegExp.$1, (RegExp.$1.length === 1) ? (o[k]) : (('00' + o[k]).substr(('' + o[k]).length)))
  }
  return fmt
}

export default {
  name: 'AppealDistribute',
  components: { StageEvidenceImportDialog },
  data() {
    return {
      dispatching: false,
      uploading: false,
      form: {
        batchName: '',
        remark: '',
        appealMonth: ''
      },
      timeRange: [],
      
      // 宸︿晶鎶樺彔闈㈡澘鏁版嵁
      treeData: [],
      rawItems: [], // 鎵佸钩鍖栨暟鎹?
      totalItems: 0,
      expandedDealers: [], // 褰撳墠灞曞紑鐨勯棬搴楋紙鏁扮粍锛屾敮鎸佸搴楀悓鏃跺睍寮€锛?
      
      // 璇佹嵁姹?
      evidencePool: [], // { uid, name, url, src, isAssigned, assignedKeys, assignedToItemKey }
      showAllFiles: false,
      
      // 鍏宠仈鍏崇郴锛?itemKey -> [fileObject]
      assignedMap: {},
      
      // 鎷栨嫿鐘舵€?
      activeDropNodeId: null,
      isPoolDropping: false,
      draggedFile: null, // 褰撳墠姝ｅ湪鎷栨嫿鐨勬枃浠?
      
      // OSS 涓婁紶鐩稿叧
      ossToken: null,
      ossTokenExpireTime: null,
      
      // 鍖归厤缁撴灉
      showMatchResult: false,
      matchResult: { success: 0, failed: 0, list: [] },
      
      // 濯掍綋棰勮
      showMediaPreview: false,
      previewFile: null,

      // 门店级证据（从阶段 1/2/4 引入，挂门店不挂指标）
      // 结构：{ [dealerCode]: [{ url, displayName, fileType, sourceItemKey, sourceSerialNo, sourceItemLabel }] }
      dealerLevelEvidences: {},
      // 阶段证据引入弹窗
      stageImportVisible: false,
      // 从路由来的 waveId、formKey（用于引入接口）
      waveId: null,
      appealFormKey: ''
    }
  },
  computed: {
    visibleFiles() {
      if (this.showAllFiles) return this.evidencePool
      return this.evidencePool.filter(f => !f.isAssigned)
    },
    // 申诉范围内的所有门店 code（用于阶段证据引入的批量匹配）
    appealDealerCodes() {
      return (this.treeData || []).map(d => d.dealerCode).filter(Boolean)
    }
  },
  mounted() {
    this.initData()
  },
  methods: {
    isImage(url) {
      return url && url.match(/\.(jpe?g|png|gif|bmp)/i)
    },
    
    toggleDealer(dealerId) {
      const idx = this.expandedDealers.indexOf(dealerId)
      if (idx > -1) {
        this.expandedDealers.splice(idx, 1)
      } else {
        this.expandedDealers.push(dealerId)
      }
    },
    
    isDealerExpanded(dealerId) {
      return this.expandedDealers.includes(dealerId)
    },
    
    async initData() {
      const answerKeysStr = this.$route.query.answerKeys
      console.log('[Distribute] Route query:', this.$route.query)
      console.log('[Distribute] answerKeysStr:', answerKeysStr)
      
      if (!answerKeysStr) {
        this.$message.error('未选择答卷')
        return
      }
      const answerKeys = answerKeysStr.split(',')
      console.log('[Distribute] answerKeys array:', answerKeys)
      
      // 璁剧疆榛樿鎵规鍚嶅拰鏃堕棿锛堜紭鍏堜粠璺敱鍙傛暟璇诲彇锛?
      const now = new Date()
      const end = new Date(now.getTime() + 7 * 24 * 3600 * 1000) // 榛樿7澶?
      this.form.batchName = this.$route.query.batchName || `${now.getFullYear()}年${now.getMonth() + 1}月${now.getDate()}日申诉`
      this.form.appealMonth = this.$route.query.appealMonth || `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`
      // 阶段证据引入需要用到波次ID + 当前申诉问卷的 formKey
      this.waveId = this.$route.query.waveId ? Number(this.$route.query.waveId) : null
      this.appealFormKey = this.$route.query.formKey || ''
      if (this.$route.query.startTime && this.$route.query.endTime) {
        this.timeRange = [this.$route.query.startTime, this.$route.query.endTime]
      } else {
        this.timeRange = [formatDate(now, 'yyyy-MM-dd HH:mm:ss'), formatDate(end, 'yyyy-MM-dd HH:mm:ss')]
      }
      
      try {
        const res = await specialApi.getAppealBatchPrepare(answerKeys)
        console.log('[Distribute] API Response:', res)
        this.processPrepareData(res)
      } catch (e) {
        console.error('API Error Details:', e)
        if (e.response) {
            console.error('Response Status:', e.response.status)
            console.error('Response Data:', e.response.data)
        }
        this.$message.error('加载答卷数据失败: ' + (e.message || '未知错误'))
      }
    },
    
    processPrepareData(data) {
      // 鏋勫缓鏍戠粨鏋勶細闂ㄥ簵 -> 棰樼洰
      // 娉ㄦ剰锛氬悗绔繑鍥炵殑 JSON 灞炴€у悕鏄?camelCase
      const tree = []
      const flatItems = []
      
      if (!data || !Array.isArray(data)) {
        console.warn('processPrepareData: data is not an array', data)
        return
      }
      
      data.forEach(dealer => {
        const items = dealer.items || dealer.Items || []
        const dealerCode = dealer.dealerCode || dealer.DealerCode || ''
        const dealerName = dealer.dealerName || dealer.DealerName || dealerCode
        const answerKey = dealer.answerKey || dealer.AnswerKey || ''
        
        const children = items.map(item => {
          const itemKey = item.itemKey || item.ItemKey || ''
          const itemLabel = item.itemLabel || item.ItemLabel || itemKey
          const serialNo = item.serialNo || item.SerialNo || ''
          const lostScore = item.lostScore || item.LostScore || 0
          const reviewRemark = item.reviewRemark || item.ReviewRemark || ''
          const assignmentKey = `${answerKey}::${itemKey}`
          
          // 鎷兼帴搴忓彿鍜屾爣绛?
          const displayLabel = serialNo ? `<span class="serial-no">${serialNo}</span> ${itemLabel}` : itemLabel
          
          flatItems.push({ 
             itemKey,
             itemLabel,
             serialNo,
             lostScore,
             reviewRemark,
             dealerName,
             dealerCode,
             answerKey,
             assignmentKey
           })
           return {
             id: `${dealerCode}_${itemKey}`,
             label: displayLabel,
             itemKey: itemKey,
             assignmentKey: assignmentKey,
             answerKey: answerKey,
             score: lostScore,
            reviewRemark: reviewRemark,
            type: 'item',
            dealerCode: dealerCode,
            dealerName: dealerName
          }
        })
        
        tree.push({
          id: dealerCode,
          dealerCode: dealerCode,
          dealerName: dealerName,
          answerKey: answerKey,
          label: `${dealerName} (${dealerCode})`,
          children: children,
          type: 'dealer'
        })
      })
      
      this.treeData = tree
      this.rawItems = flatItems
      this.totalItems = flatItems.length
      
      // 榛樿鍙睍寮€绗竴瀹堕棬搴?
      if (tree.length > 0) {
        this.expandedDealers = [tree[0].id]
      }
      
      // 鍒濆鍖?assignedMap
      flatItems.forEach(item => {
        this.$set(this.assignedMap, item.assignmentKey, [])
      })
    },
    
    // ----------- 鎷栨嫿閫昏緫 (鏂囦欢姹?-> 鏍? -----------
    
    handleDragStart(e, file) {
      this.draggedFile = file
      e.dataTransfer.effectAllowed = 'copyMove'
      // 鍙互鍦ㄨ繖閲岃缃嫋鎷芥椂鐨勬浛鎹㈠浘鍍?
    },
    
    handleDragOver(e, data) {
      // 浠呴樆姝㈤粯璁よ涓哄厑璁?drop锛屼笉鍋氱姸鎬佸垏鎹?
      if (data.type === 'item') {
        e.dataTransfer.dropEffect = 'copy'
      }
    },
    
    handleDragEnter(e, data) {
      if (data.type === 'item') {
        // 璁℃暟鍣?+1锛岄娆¤繘鍏ユ椂璁剧疆楂樹寒
        if (!this._dragCountMap) this._dragCountMap = {}
        if (!this._dragCountMap[data.id]) this._dragCountMap[data.id] = 0
        this._dragCountMap[data.id]++
        this.activeDropNodeId = data.id
      }
    },
    
    handleDragLeave(e, data) {
      if (!data || data.type !== 'item') return
      if (!this._dragCountMap) this._dragCountMap = {}
      if (this._dragCountMap[data.id]) {
        this._dragCountMap[data.id]--
      }
      // 璁℃暟鍣ㄥ綊闆舵墠娓呴櫎楂樹寒
      if (!this._dragCountMap[data.id]) {
        if (this.activeDropNodeId === data.id) {
          this.activeDropNodeId = null
        }
      }
    },
    
    handleDropOnNode(e, data) {
      this.activeDropNodeId = null
      if (this._dragCountMap) this._dragCountMap[data.id] = 0
      if (data.type !== 'item') return
      
      if (this.draggedFile) {
        // 鏉ヨ嚜搴旂敤鍐呯殑鎷栨嫿
        this.assignFileToItem(this.draggedFile, data.assignmentKey)
        this.draggedFile = null
      } else if (e.dataTransfer.files.length > 0) {
        // 鏉ヨ嚜鎿嶄綔绯荤粺鐨勭洿鎺ユ嫋鎷?(鏆備笉鏀寔鐩存帴鎷栨嫿鍒?Item 涓婁笂浼狅紝寤鸿鍏堟嫋鍒版睜瀛?
        this.$message.warning('请先将文件上传到右侧证据池')
      }
    },
    
    assignFileToItem(file, assignmentKey, allowMultiple = false) {
      if (!Array.isArray(file.assignedKeys)) {
        this.$set(file, 'assignedKeys', [])
      }
      if (!allowMultiple && file.assignedKeys.length > 0) {
        ;[...file.assignedKeys].forEach(key => this.removeAssignment(key, null, file.uid))
      }
      const list = this.assignedMap[assignmentKey] || []
      if (list.some(f => f.uid === file.uid)) {
        return
      }
      list.push(file)
      this.$set(this.assignedMap, assignmentKey, list)
      file.assignedKeys.push(assignmentKey)
      file.isAssigned = file.assignedKeys.length > 0
      file.assignedToItemKey = file.assignedKeys.length === 1 ? file.assignedKeys[0] : null
    },
    removeFromPool(file) {
      if (file.isAssigned && Array.isArray(file.assignedKeys) && file.assignedKeys.length > 0) {
        ;[...file.assignedKeys].forEach(key => this.removeAssignment(key, null, file.uid))
      }
      const idx = this.evidencePool.findIndex(f => f.uid === file.uid)
      if (idx >= 0) {
        this.evidencePool.splice(idx, 1)
      }
    },
    removeAssignment(assignmentKey, index, fileUid = null) {
      const list = this.assignedMap[assignmentKey]
      if (!list) return
      let removedFile = null
      if (fileUid) {
        const idx = list.findIndex(f => f.uid === fileUid)
        if (idx > -1) {
          removedFile = list.splice(idx, 1)[0]
        }
      } else {
        removedFile = list.splice(index, 1)[0]
      }
      if (removedFile) {
        const poolFile = this.evidencePool.find(f => f.uid === removedFile.uid)
        if (poolFile) {
          const nextAssignedKeys = (poolFile.assignedKeys || []).filter(key => key !== assignmentKey)
          this.$set(poolFile, 'assignedKeys', nextAssignedKeys)
          poolFile.isAssigned = nextAssignedKeys.length > 0
          poolFile.assignedToItemKey = nextAssignedKeys.length === 1 ? nextAssignedKeys[0] : null
        }
      }
      if (list.length === 0) {
        this.$delete(this.assignedMap, assignmentKey)
      }
    },
    // ----------- 涓婁紶閫昏緫 -----------
    
    triggerFolderUpload() {
      this.$refs.folderInput.click()
    },
    
    handleFolderSelect(e) {
      const files = e.target.files
      if (files.length > 0) {
        // 杞崲涓烘櫘閫氭暟缁勶紝骞朵繚鐣?webkitRelativePath 浣滀负 relativePath
        const fileList = Array.from(files).map(file => {
          // webkitRelativePath 鍖呭惈瀹屾暣鐩稿璺緞锛屽 "VCQ01/B1/file.jpg"
          file.relativePath = file.webkitRelativePath || file.name
          return file
        })
        this.batchUpload(fileList)
      }
      // 娓呯┖ value 浠ヤ究涓嬫瑙﹀彂
      e.target.value = ''
    },
    
    // 璇佹嵁姹犳嫋鎷戒笂浼?
    handlePoolDragOver(e) {
      e.dataTransfer.dropEffect = 'copy'
      this.isPoolDropping = true
    },
    
    handlePoolDragLeave(e) {
      this.isPoolDropping = false
    },
    
    async handlePoolDrop(e) {
      this.isPoolDropping = false
      const items = e.dataTransfer.items
      if (!items) return
      
      const fileList = []
      const scanPromises = []
      
      for (let i = 0; i < items.length; i++) {
        const item = items[i].webkitGetAsEntry ? items[i].webkitGetAsEntry() : (items[i].getAsEntry ? items[i].getAsEntry() : null)
        if (item) {
          scanPromises.push(this.traverseFileTree(item, fileList, ''))
        } else {
          const file = items[i].getAsFile()
          if (file) {
            file.relativePath = file.name // 鍗曟枃浠舵棤璺緞
            fileList.push(file)
          }
        }
      }
      
      await Promise.all(scanPromises)
      if (fileList.length > 0) {
        this.batchUpload(fileList)
      }
    },
    
    // 閫掑綊閬嶅巻鏂囦欢澶癸紝淇濈暀鐩稿璺緞
    traverseFileTree(item, fileList, parentPath = '') {
      return new Promise((resolve) => {
        const currentPath = parentPath ? `${parentPath}/${item.name}` : item.name
        
        if (item.isFile) {
          item.file(file => {
            // 灏嗙浉瀵硅矾寰勯檮鍔犲埌鏂囦欢瀵硅薄涓?
            file.relativePath = currentPath
            fileList.push(file)
            resolve()
          })
        } else if (item.isDirectory) {
          const dirReader = item.createReader()
          const readEntries = () => {
             dirReader.readEntries(entries => {
               if (entries.length === 0) {
                 resolve()
               } else {
                 // 浼犻€掑綋鍓嶇洰褰曡矾寰勭粰瀛愰」
                 const subPromises = entries.map(entry => this.traverseFileTree(entry, fileList, currentPath))
                 Promise.all(subPromises).then(() => readEntries())
               }
             })
          }
           readEntries() 
        } else {
          resolve()
        }
      })
    },

    async batchUpload(files) {
      if (files.length > 500) {
        this.$confirm('文件较多，上传可能需要较长时间，是否继续？', '提示')
          .then(() => this.doBatchUpload(files))
          .catch(() => {})
      } else {
        this.doBatchUpload(files)
      }
    },
    
    async doBatchUpload(files) {
      this.uploading = true
      let successCount = 0
      let failCount = 0
      
      // 骞跺彂鎺у埗锛屾瘡娆?3 涓?
      const CONCURRENCY = 3
      const queue = [...files]
      
      const worker = async () => {
        while (queue.length > 0) {
          const file = queue.shift()
          try {
            // 浣跨敤鐜版湁鐨?ossUpload 宸ュ叿
            const result = await smartUpload(file, 'appeal/evidence')
            this.addToPool(file, { url: result.url })
            successCount++
          } catch (e) {
            console.error(`上传失败: ${file.name}`, e)
            failCount++
          }
        }
      }
      
      const workers = []
      for (let i = 0; i < CONCURRENCY; i++) {
        workers.push(worker())
      }
      
      await Promise.all(workers)
      this.uploading = false
      
      if (failCount > 0) {
        this.$message.warning(`成功上传 ${successCount} 个文件，${failCount} 个失败`)
      } else {
        this.$message.success(`成功上传 ${successCount} 个文件`)
      }
    },
    
    // 鑾峰彇 OSS STS Token
    async getOssToken() {
      // 妫€鏌ョ紦瀛樻槸鍚︽湁鏁堬紙鎻愬墠5鍒嗛挓杩囨湡锛?
      if (this.ossToken && this.ossTokenExpireTime) {
        const now = new Date().getTime()
        if (now < this.ossTokenExpireTime - 5 * 60 * 1000) {
          return this.ossToken
        }
      }
      
      const res = await axios.get('/oss/sts-token')
      const data = res?.data?.data || res?.data || {}
      
      // 鍚庣杩斿洖 PascalCase锛屽吋瀹逛袱绉嶆牸寮?
      this.ossToken = {
        accessKeyId: data.AccessKeyId || data.accessKeyId,
        accessKeySecret: data.AccessKeySecret || data.accessKeySecret,
        securityToken: data.SecurityToken || data.securityToken,
        region: data.Region || data.region,
        bucket: data.Bucket || data.bucket
      }
      
      const expiration = data.Expiration || data.expiration
      if (expiration) {
        this.ossTokenExpireTime = new Date(expiration).getTime()
      }
      
      return this.ossToken
    },
    
    // 涓婁紶鍗曚釜鏂囦欢鍒?OSS
    uploadToOss(file) {
      return new Promise((resolve, reject) => {
        const token = this.ossToken
        if (!token) {
          reject(new Error('Missing OSS token'))
          return
        }
        
        // 鏋勫缓涓婁紶璺緞
        const ext = file.name.substring(file.name.lastIndexOf('.')).toLowerCase()
        const date = new Date().toISOString().slice(0, 10).replace(/-/g, '')
        const fileUuid = uuid()
        const objectPath = `appeal/evidence/${date}/${fileUuid}${ext}`
        
        // OSS endpoint
        const endpoint = `https://${token.bucket}.${token.region}.aliyuncs.com`
        
        // 浣跨敤 XMLHttpRequest PUT 鏂瑰紡涓婁紶
        const xhr = new XMLHttpRequest()
        xhr.open('PUT', `${endpoint}/${objectPath}`, true)
        xhr.setRequestHeader('x-oss-security-token', token.securityToken)
        
        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            const url = `${endpoint}/${objectPath}`
            resolve(url)
          } else {
            reject(new Error(`Upload failed: ${xhr.statusText}`))
          }
        }
        
        xhr.onerror = () => {
          reject(new Error('Network error'))
        }
        
        xhr.send(file)
      })
    },
    
    uploadFile(param) {
      const file = param.file
      this.doBatchUpload([file])
    },
    
    addToPool(file, res) {
      // 鏋勯€犳睜瀵硅薄锛屼繚鐣欑浉瀵硅矾寰勭敤浜庢櫤鑳藉尮閰?
      // OSS 杩斿洖鐨?URL 宸茬粡绛惧悕锛屽彲浠ョ洿鎺ヤ娇鐢?
      const ext = file.name.substring(file.name.lastIndexOf('.')).toLowerCase()
      const fileType = this.getFileTypeByExt(ext)
      
      // 璋冭瘯锛氭鏌?relativePath 鏄惁姝ｇ‘淇濈暀
      
      const poolItem = {
        uid: uuid(),
        name: file.name,
        relativePath: file.relativePath || file.name, // 淇濈暀鐩稿璺緞
        url: res.url,
        src: res.url, // 鐩存帴浣跨敤 OSS 杩斿洖鐨勭鍚?URL
        fileType: fileType, // 'image', 'video', 'audio', 'document'
        isAssigned: false,
        assignedKeys: [],
        assignedToItemKey: null
      }
      this.evidencePool.unshift(poolItem)
    },
    
    // 鏍规嵁鎵╁睍鍚嶈幏鍙栨枃浠剁被鍨?
    getFileTypeByExt(ext) {
      if (['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.webp', '.svg'].includes(ext)) return 'image'
      if (['.mp4', '.avi', '.mov', '.wmv', '.flv', '.mkv', '.webm'].includes(ext)) return 'video'
      if (['.mp3', '.wav', '.ogg', '.aac', '.flac', '.m4a'].includes(ext)) return 'audio'
      return 'document'
    },
    
    // 鎵撳紑濯掍綋棰勮
    openMediaPreview(file) {
      if (file.fileType === 'video' || file.fileType === 'audio') {
        this.previewFile = file
        this.showMediaPreview = true
      }
    },
    
    // 鍏抽棴濯掍綋棰勮
    closeMediaPreview() {
      // 鍋滄鎾斁
      if (this.$refs.previewVideo) {
        this.$refs.previewVideo.pause()
      }
      if (this.$refs.previewAudio) {
        this.$refs.previewAudio.pause()
      }
      this.showMediaPreview = false
      this.previewFile = null
    },
    
    // 鎵撳紑鏂囨。鏂囦欢锛堝湪鏂版爣绛鹃〉锛?
    openDocument(file) {
      if (file.src) {
        window.open(file.src, '_blank')
      } else {
        this.$message.warning('文件地址无效')
      }
    },

    // ----------- 门店级证据（从阶段 1/2/4 引入）-----------
    openStageImport() {
      if (!this.waveId) {
        this.$message.warning('当前批次没有关联波次，无法引入阶段证据')
        return
      }
      if (!this.appealDealerCodes || this.appealDealerCodes.length === 0) {
        this.$message.warning('当前申诉范围没有门店')
        return
      }
      this.stageImportVisible = true
    },
    handleStageImported(groups) {
      // groups: [{ dealerCode, dealerName, evidences: [{ url, displayName, fileType, ... }] }]
      if (!Array.isArray(groups)) return
      const next = { ...this.dealerLevelEvidences }
      let addedCount = 0
      let skippedCount = 0
      groups.forEach(g => {
        if (!g || !g.dealerCode) return
        const existing = next[g.dealerCode] ? [...next[g.dealerCode]] : []
        const existingUrls = new Set(existing.map(e => e.url))
        ;(g.evidences || []).forEach(ev => {
          if (!ev || !ev.url) return
          if (existingUrls.has(ev.url)) {
            skippedCount++
            return  // URL 去重
          }
          existing.push(ev)
          existingUrls.add(ev.url)
          addedCount++
        })
        next[g.dealerCode] = existing
      })
      this.dealerLevelEvidences = next
      // 子组件不弹 toast，由父组件按真实新增数提示，避免"显示已引入 5 个但实际新增 0 个"的误导
      if (addedCount > 0) {
        const tail = skippedCount > 0 ? `，${skippedCount} 个已引入过自动跳过` : ''
        this.$message.success(`新增 ${addedCount} 个文件到门店卡片${tail}`)
      } else {
        this.$message.info('没有新文件可引入（所选题目下文件已全部引入过）')
      }
    },
    removeDealerLevelEvidence(dealerCode, index) {
      const list = this.dealerLevelEvidences[dealerCode]
      if (!list) return
      const updated = [...list]
      updated.splice(index, 1)
      const next = { ...this.dealerLevelEvidences }
      if (updated.length === 0) delete next[dealerCode]
      else next[dealerCode] = updated
      this.dealerLevelEvidences = next
    },
    openDealerEvidence(ev) {
      if (!ev || !ev.url) return
      if (ev.fileType === 'image') {
        // 用新标签页预览图片（简单可靠；如需内嵌查看器可改用 el-image-viewer）
        window.open(ev.url, '_blank')
      } else if (ev.fileType === 'audio' || ev.fileType === 'video') {
        this.previewFile = { name: ev.displayName, src: ev.url, fileType: ev.fileType }
        this.showMediaPreview = true
      } else {
        window.open(ev.url, '_blank')
      }
    },
    dealerEvIconClass(fileType) {
      switch (fileType) {
        case 'image': return 'el-icon-picture-outline'
        case 'audio': return 'el-icon-headset'
        case 'video': return 'el-icon-video-camera'
        case 'signature': return 'el-icon-edit-outline'
        default: return 'el-icon-document'
      }
    },
    dealerEvIconColor(fileType) {
      switch (fileType) {
        case 'image': return '#8b5cf6'
        case 'audio': return '#22c55e'
        case 'video': return '#f59e0b'
        case 'signature': return '#ec4899'
        default: return '#3b82f6'
      }
    },
    
    // ----------- 鏅鸿兘鍖归厤 -----------
    
    smartMatch() {
      const result = { success: 0, failed: 0, storeLevel: 0, list: [] }
      
      // 棰勫鐞嗭細涓烘瘡涓鐩彁鍙栫煭鐮?
      const itemsWithShortCode = this.rawItems.map(item => {
        // 鍏堝幓闄?HTML 鏍囩锛屽 <p><strong>B1.涓诲姩杩庢帴</strong></p> -> B1.涓诲姩杩庢帴
        const plainLabel = (item.itemLabel || '').replace(/<[^>]+>/g, '').replace(/&nbsp;/g, ' ').trim()
        // 鐒跺悗鎻愬彇鐭爜 B1, D4 绛?
        // 兼容：A2 / AB2 / A2-1 / "1." / "第1题" / "(A2)"；大小写忽略，统一转大写
        // 优先用后端 serialNo(B1/C1...);为空时再从 label 抽
        let shortCode = (item.serialNo || '').trim().toUpperCase() || null
        if (!shortCode) {
          const codeMatch =
            plainLabel.match(/^([A-Z]{1,3}\d+(?:-\d+)?)/i) ||
            plainLabel.match(/^第?\s*(\d+)\s*[题.．、]/) ||
            plainLabel.match(/[（(]([A-Z]{1,3}\d+(?:-\d+)?|\d+)[)）]/i)
          shortCode = codeMatch ? codeMatch[1].toUpperCase() : null
        }
        return { ...item, shortCode, plainLabel }
      })
      
      // 鏋勫缓搴楅摵 -> 棰樼洰鏄犲皠
      const storeItemsMap = {}
      itemsWithShortCode.forEach(item => {
        if (!storeItemsMap[item.dealerCode]) {
          storeItemsMap[item.dealerCode] = []
        }
        storeItemsMap[item.dealerCode].push(item)
      })
      
      
      // ========== 绗竴閬嶏細绮剧‘鍖归厤锛堝簵閾?+ 棰樼洰锛?==========
      this.evidencePool.forEach(file => {
        if (file.isAssigned) return
        
        const matchTarget = (file.relativePath || file.name || '').toUpperCase()

        for (const item of itemsWithShortCode) {
          // 妫€鏌ユ槸鍚﹀寘鍚簵鍙?
          if (!item.shortCode) continue
          const hasDealer = matchTarget.includes((item.dealerCode || '').toUpperCase())
          // 妫€鏌ユ槸鍚﹀寘鍚煭鐮?
          // 边界匹配：-A2. / _A2_ 命中 A2，但 A20 不会命中 A2
          const escaped = item.shortCode.replace(/[-/\\^$*+?.()|[\]{}]/g, '\\$&')
          const boundaryRe = new RegExp(`(?:^|[^A-Z0-9])${escaped}(?:[^A-Z0-9]|$)`)
          const hasShortCode = boundaryRe.test(matchTarget)
          
          // 璋冭瘯锛氭樉绀哄崟涓瘮杈?
          
          if (hasDealer && hasShortCode) {
            this.assignFileToItem(file, item.assignmentKey)
            result.success++
            result.list.push({ 
              name: file.name, 
              status: 'success', 
              target: `${item.dealerName} - ${item.itemLabel}` 
            })
            break
          }
        }
      })
      
      
      // ========== 缁熻鏈尮閰嶆枃浠?==========
      this.evidencePool.forEach(file => {
        if (!file.isAssigned) {
          result.failed++
          result.list.push({ 
            name: file.name, 
            status: 'failed', 
            target: '无匹配，需手动分配'
          })
        }
      })
      
      this.matchResult = result
      this.showMatchResult = true
    },
    
    // ----------- 鎻愪氦 -----------
    
    async handleDispatch() {
      if (!this.form.batchName) {
        this.$message.error('请输入批次名称')
        return
      }
      if (!this.timeRange || this.timeRange.length !== 2) {
        this.$message.error('请选择申诉时间')
        return
      }
      
      const answerKeys = this.$route.query.answerKeys.split(',')
      
      // 1. 鍏堟娴嬮噸澶?
      try {
        const statusRes = await axios.post('/Appeal/answer/status', answerKeys)
        const statusMap = statusRes?.data?.data || statusRes?.data || statusRes || {}
        const duplicateKeys = answerKeys.filter(k => statusMap[k])
        
        if (duplicateKeys.length > 0) {
          // 鎵惧嚭閲嶅闂ㄥ簵鍚嶇О
          const duplicateNames = duplicateKeys.map(k => {
            const item = this.rawItems.find(i => i.answerKey === k)
            return item ? `${item.dealerName} (${item.dealerCode})` : k
          })
          
          const msg = `以下 ${duplicateNames.length} 家门店已下发过申诉：\n\n${duplicateNames.join('\n')}\n\n是否覆盖已有申诉记录并重新下发？`
          
          try {
            await this.$confirm(msg, '检测到重复门店', {
              type: 'warning',
              customClass: 'modern-confirm',
              confirmButtonText: '覆盖下发',
              cancelButtonText: '取消',
              dangerouslyUseHTMLString: false
            })
            // 鐢ㄦ埛閫夋嫨瑕嗙洊 鈫?鍏堝垹闄ゆ棫璁板綍
            await axios.post('/Appeal/batch/delete-by-answers', { answerKeys: duplicateKeys })
          } catch {
            return // 鐢ㄦ埛鍙栨秷
          }
        }
      } catch (e) {
        console.warn('检测重复失败，继续下发', e)
      }
      
      // 2. 纭涓嬪彂
      try {
        await this.$confirm(`确认下发申诉批次 "${this.form.batchName}" 吗？`, '确认下发', { 
          type: 'warning',
          customClass: 'modern-confirm',
          confirmButtonText: '✈ 确认下发',
          cancelButtonText: '取消'
        })
      } catch {
        return
      }
      
      // 3. 鎵ц涓嬪彂
      this.dispatching = true
      try {
        const dispatchEvidences = {}
        
        Object.keys(this.assignedMap).forEach(assignmentKey => {
          const files = this.assignedMap[assignmentKey]
          if (files.length > 0) {
            const rawItem = this.rawItems.find(i => i.assignmentKey === assignmentKey)
            if (rawItem && rawItem.answerKey) {
              const aKey = rawItem.answerKey
              if (!dispatchEvidences[aKey]) {
                dispatchEvidences[aKey] = []
              }
              dispatchEvidences[aKey].push({
                itemKey: rawItem.itemKey,
                evidenceUrls: JSON.stringify(files.map(f => ({ name: f.name, url: f.url })))
              })
            }
          }
        })
        
        // 门店级证据（从阶段 1/2/4 引入，挂门店不挂指标）
        const dealerLevelEvidences = {}
        Object.keys(this.dealerLevelEvidences).forEach(dealerCode => {
          const list = this.dealerLevelEvidences[dealerCode]
          if (!list || list.length === 0) return
          dealerLevelEvidences[dealerCode] = list.map(ev => ({
            url: ev.url,
            displayName: ev.displayName,
            sourceStagePhase: ev.sourceStagePhase,
            sourceItemKey: ev.sourceItemKey,
            sourceItemLabel: ev.sourceItemLabel,
            sourceSerialNo: ev.sourceSerialNo,
            fileType: ev.fileType
          }))
        })

        const postData = {
          batchName: this.form.batchName,
          startTime: this.timeRange[0],
          endTime: this.timeRange[1],
          remark: this.form.remark,
          appealMonth: this.form.appealMonth,
          answerKeys: answerKeys,
          dispatchEvidences: dispatchEvidences,
          dealerLevelEvidences: dealerLevelEvidences,
          projectId: Number(this.$route.query.projectId || 0),
          formKey: this.$route.query.formKey || ''
        }
        
        await specialApi.createAppealBatch(postData)
        this.$message.success('下发成功')
        this.$router.back()
      } catch (e) {
        console.error(e)
      } finally {
        this.dispatching = false
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.appeal-distribute {
  padding: 0;
  background: #f5f7fa;
  min-height: 100vh;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
}

// ========== 椤堕儴瀵艰埅 ==========
.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #fff;
  padding: 16px 24px;
  border-bottom: 1px solid #e5e7eb;
  
  .left {
    display: flex;
    align-items: center;
    gap: 12px;
    
    .back-icon {
      font-size: 20px;
      color: #6b7280;
      cursor: pointer;
      padding: 4px;
      border-radius: 6px;
      transition: all 0.2s;
      
      &:hover {
        color: #3b82f6;
        background: #eff6ff;
      }
    }
    
    .header-icon {
      font-size: 22px;
      color: #3b82f6;
    }
    
    .title {
      font-size: 18px;
      font-weight: 700;
      color: #111827;
    }
  }
  
  .right {
    display: flex;
    gap: 10px;
    align-items: center;
    
    ::v-deep .el-button--primary {
      border-radius: 20px;
      padding: 10px 24px;
      background-color: #1d4ed8 !important;
      border-color: #1d4ed8 !important;
      color: #fff !important;
    }
  }
}

// ========== 鍩虹淇℃伅琛?==========
.info-row {
  display: flex;
  gap: 20px;
  padding: 20px 24px;
  background: #fff;
  border-bottom: 1px solid #f0f0f0;
  
  .info-field {
    flex: 1;
    
    label {
      display: block;
      font-size: 13px;
      font-weight: 600;
      color: #6b7280;
      margin-bottom: 6px;
    }
    
    .el-input, .el-date-editor {
      width: 100%;
    }
  }
}

// ========== 宸ヤ綔鍖?==========
.workspace {
  flex: 1;
  display: flex;
  gap: 20px;
  padding: 20px 24px;
  min-height: 0;
  overflow: hidden;
  
  .panel {
    background: #fff;
    border-radius: 12px;
    display: flex;
    flex-direction: column;
    border: 1px solid #e5e7eb;
    overflow: hidden;
    
    .panel-header {
      padding: 16px 20px;
      border-bottom: 1px solid #f0f0f0;
      display: flex;
      justify-content: space-between;
      align-items: center;
      flex-shrink: 0;
      
      .left, .right {
        display: flex;
        align-items: center;
        gap: 8px;
      }
      
      .title {
        font-size: 15px;
        font-weight: 600;
        color: #111827;
      }
      
      .toggle-label {
        font-size: 13px;
        color: #9ca3af;
      }
    }
    
    .panel-toolbar {
      padding: 12px 20px;
      border-bottom: 1px solid #f0f0f0;
      display: flex;
      gap: 10px;
      flex-shrink: 0;
      
      // 寮哄埗鎵€鏈夋寜閽兌鍥婂舰
      ::v-deep .el-button {
        border-radius: 20px !important;
        padding: 8px 18px;
        font-size: 13px;
      }
      
      ::v-deep .el-button--primary {
        background-color: #3b82f6;
        border-color: #3b82f6;
      }
      
      // plain/default 鎸夐挳寮哄埗鐏拌壊杈规+榛戣壊鏂囧瓧
      ::v-deep .el-button--default {
        color: #374151 !important;
        border-color: #d1d5db !important;
        background: #fff !important;
        
        i {
          color: #374151 !important;
        }
        
        &:hover {
          color: #3b82f6 !important;
          border-color: #3b82f6 !important;
          
          i { color: #3b82f6 !important; }
        }
      }
    }
    
    .panel-footer {
      padding: 10px 20px;
      border-top: 1px solid #f0f0f0;
      display: flex;
      justify-content: space-between;
      align-items: center;
      font-size: 12px;
      color: #3b82f6;
      flex-shrink: 0;
    }
    
    .panel-body {
      flex: 1;
      overflow-y: auto;
      padding: 16px 20px;
      position: relative;
    }
  }
  
  // ====== 宸︿晶闈㈡澘 ======
  .left-panel {
    flex: 1;
    min-width: 0;
    
    // 鑷畾涔?el-collapse 鏍峰紡
    ::v-deep .el-collapse {
      border: none;
    }
    
    ::v-deep .el-collapse-item {
      margin-bottom: 12px;
      border: 1px solid #e5e7eb;
      border-radius: 10px;
      overflow: hidden;
      
      .el-collapse-item__header {
        height: 52px;
        line-height: 52px;
        padding: 0 16px;
        font-size: 15px;
        font-weight: 600;
        color: #111827;
        border-bottom: none;
        background: #f9fafb;
      }
      
      .el-collapse-item__wrap {
        border-bottom: none;
      }
      
      .el-collapse-item__content {
        padding: 0 16px 16px;
      }
    }
    
    .dealer-title {
      font-weight: 600;
      font-size: 15px;
      color: #111827;
    }
    
    // 棰樼洰鍗＄墖
    .question-card {
      padding: 16px 0;
      border-bottom: 1px solid #f0f0f0;
      
      &:last-child {
        border-bottom: none;
      }
      
      &.drop-active {
        background: #eff6ff;
        border-radius: 8px;
        padding: 16px;
        margin: -8px;
      }
    }
    
    .question-header {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      margin-bottom: 10px;
      
      .question-label {
        flex: 1;
        font-size: 14px;
        font-weight: 500;
        color: #1e40af;
        line-height: 1.5;
        
        ::v-deep p {
          margin: 0;
        }
      }
      
      .question-id {
        font-size: 12px;
        color: #9ca3af;
        white-space: nowrap;
        margin-left: 16px;
        flex-shrink: 0;
      }
    }
    
    // 鎷栨嫿涓婁紶鍖?
    .drop-zone {
      padding: 28px 16px;
      color: #9ca3af;
      font-size: 13px;
      background: #fff;
      border: 1.5px dashed #d1d5db;
      border-radius: 10px;
      text-align: center;
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 8px;
      transition: all 0.2s;
      
      i {
        font-size: 24px;
        color: #d1d5db;
      }
      
      &.drop-zone-active,
      &:hover {
        border-color: #3b82f6;
        background: #eff6ff;
        
        i { color: #3b82f6; }
        span { color: #3b82f6; }
      }
    }
    
    // 宸插垎閰嶆枃浠?
    .assigned-files {
       display: flex;
       flex-wrap: wrap;
       gap: 10px;
       
       .file-thumb {
         width: 70px;
         height: 70px;
         border: 1px solid #e5e7eb;
         border-radius: 8px;
         position: relative;
         background: #f9fafb;
         overflow: visible;
         
         .img {
           width: 100%;
           height: 100%;
           object-fit: cover;
           border-radius: 8px;
         }
         
         .file-icon {
           width: 100%;
           height: 100%;
           display: flex;
           align-items: center;
           justify-content: center;
           font-size: 28px;
           color: #9ca3af;
         }
         
         .remove-btn {
           position: absolute;
           top: -6px;
           right: -6px;
           width: 18px;
           height: 18px;
           background: #ef4444;
           color: #fff;
           border-radius: 50%;
           display: none;
           align-items: center;
           justify-content: center;
           font-size: 12px;
           cursor: pointer;
           box-shadow: 0 2px 4px rgba(0,0,0,0.15);
         }
         
         &:hover .remove-btn {
           display: flex;
         }
         
         .file-name {
           position: absolute;
           bottom: 0;
           left: 0;
           right: 0;
           background: rgba(0,0,0,0.6);
           color: #fff;
           font-size: 10px;
           padding: 2px 4px;
           text-overflow: ellipsis;
           overflow: hidden;
           white-space: nowrap;
           border-radius: 0 0 8px 8px;
         }
       }
    }
  }
  
  // ====== 鍙充晶闈㈡澘 ======
  .right-panel {
    flex: 1;
    min-width: 0;
    
    .empty-pool {
      height: 100%;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      color: #9ca3af;
      padding: 40px 20px;
      border: 1.5px dashed #d1d5db;
      border-radius: 12px;
      margin: 0;
      
      .empty-icon {
        width: 88px;
        height: 88px;
        border-radius: 50%;
        background: #e8f0fe;
        display: flex;
        align-items: center;
        justify-content: center;
        margin-bottom: 24px;
        
        i {
          font-size: 38px;
          color: #3b82f6;
        }
      }
      
      h4 {
        font-size: 16px;
        font-weight: 700;
        color: #111827;
        margin: 0 0 8px;
      }
      
      p {
        font-size: 13px;
        color: #9ca3af;
        margin: 0 0 24px;
        text-align: center;
        line-height: 1.6;
      }
      
      .file-types {
        display: flex;
        gap: 16px;
        flex-wrap: wrap;
        justify-content: center;
        
        span {
          display: flex;
          align-items: center;
          gap: 6px;
          font-size: 13px;
          color: #9ca3af;
          background: #fff;
          padding: 6px 14px;
          border-radius: 8px;
          border: 1px solid #e5e7eb;
          
          i {
            font-size: 16px;
            color: #6b7280;
          }
        }
      }
    }
    
    .file-grid {
      display: flex;
      flex-wrap: wrap;
      gap: 15px;
      
      .pool-file-item {
        width: 100px;
        cursor: grab;
        
        .file-content {
          width: 100px;
          height: 100px;
          border: 1px solid #e5e7eb;
          border-radius: 8px;
          background: #f9fafb;
          position: relative;
          overflow: hidden;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.2s;
          
          &:hover {
            border-color: #3b82f6;
            box-shadow: 0 2px 8px rgba(59, 130, 246, 0.12);
          }
          
          .file-img {
            width: 100%;
            height: 100%;
          }
          
          .file-icon-large {
            font-size: 40px;
            color: #9ca3af;
            
            &.file-video {
              color: #f59e0b;
            }
            &.file-audio {
              color: #22c55e;
            }
            &.file-document {
              color: #3b82f6;
            }
          }
          
          .status-badge {
            position: absolute;
            right: 0;
            bottom: 0;
            background: #22c55e;
            color: #fff;
            padding: 2px 4px;
          }

          .pool-remove-btn {
            position: absolute;
            top: 2px;
            right: 2px;
            width: 20px;
            height: 20px;
            border-radius: 50%;
            background: #ef4444;
            color: #fff;
            display: none;
            align-items: center;
            justify-content: center;
            font-size: 12px;
            cursor: pointer;
            z-index: 2;
            
            &:hover {
              background: #dc2626;
            }
          }
          
          &:hover .pool-remove-btn {
            display: flex;
          }
        }
        
        .file-name {
          margin-top: 5px;
          font-size: 12px;
          color: #6b7280;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          text-align: center;
        }
      }
    }
    
    .drop-mask {
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: rgba(59, 130, 246, 0.08);
      border: 2px dashed #3b82f6;
      border-radius: 8px;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      z-index: 10;
      color: #3b82f6;
      
      i {
        font-size: 48px;
        margin-bottom: 20px;
      }
    }
  }
}

// 搴曢儴鎻愮ず
.bottom-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 24px;
  background: #fff;
  border-top: 1px solid #e5e7eb;
  font-size: 13px;
  color: #9ca3af;
}

// 瀹℃牳澶囨敞鎻愮ず
.review-remark-hint {
  margin-top: 4px;
  padding: 6px 10px;
  background: #fffbe6;
  border: 1px solid #ffe58f;
  border-radius: 4px;
  font-size: 12px;
  color: #8c6e14;
  line-height: 1.5;
  
  i {
    margin-right: 4px;
    color: #d48806;
  }
}

// 濯掍綋棰勮
.media-preview-container {
  text-align: center;
  
  .preview-video {
    max-width: 100%;
    max-height: 400px;
    border-radius: 8px;
  }
  
  .preview-audio {
    width: 100%;
    margin: 20px 0;
  }
}

// ========== 鍝嶅簲寮?==========
@media (max-width: 768px) {
  .info-row {
    flex-direction: column;
    gap: 12px;
  }
  
  .workspace {
    flex-direction: column;
    padding: 12px;
    gap: 12px;
    
    .left-panel, .right-panel {
      flex: none;
      max-height: 50vh;
    }
  }
}

// ========== 搴忓彿鏍囩 ==========
::v-deep .serial-no {
  display: inline-block;
  background: #e8f0fe;
  color: #4c6ef5;
  padding: 1px 6px;
  border-radius: 4px;
  font-weight: 600;
  font-size: 11px;
  margin-right: 4px;
  vertical-align: middle;
}

.dealer-tag {
  display: inline-block;
  background: #fff7ed;
  color: #e6a23c;
  padding: 1px 8px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 500;
  margin-right: 6px;
  border: 1px solid #fde3c0;
}

// ========== 鑷畾涔夋姌鍙犻潰鏉匡紙搴楃淮搴︼級 ==========
.dealer-group {
  margin-bottom: 12px;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  overflow: hidden;
  background: #fff;
  
  .dealer-group-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0 16px;
    height: 48px;
    background: linear-gradient(135deg, #f0f4ff 0%, #f8faff 100%);
    border-bottom: 1px solid #e5e7eb;
    cursor: pointer;
    user-select: none;
    transition: background 0.2s;
    
    &:hover {
      background: linear-gradient(135deg, #e8f0fe 0%, #f0f4ff 100%);
    }
    
    .dealer-group-left {
      display: flex;
      align-items: center;
      gap: 8px;
      
      .expand-arrow {
        font-size: 12px;
        color: #6b7280;
        transition: transform 0.2s;
      }
      
      .dealer-icon {
        color: #3b82f6;
        font-size: 16px;
      }
      
      .dealer-group-name {
        font-size: 14px;
        font-weight: 600;
        color: #1e3a5f;
      }
    }
  }
  
  .dealer-group-body {
    padding: 12px 16px;
  }

  /* 门店级证据区 */
  .dealer-level-evidence {
    margin-bottom: 12px;
    padding: 10px 12px;
    background: #fffaeb;
    border: 1px dashed #f59e0b;
    border-radius: 8px;
  }
  .dealer-level-evidence-header {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 12px;
    font-weight: 600;
    color: #92400e;
    margin-bottom: 8px;
    i { font-size: 14px; }
  }
  .dealer-level-evidence-list {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }
  .dealer-level-evidence-item {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 4px 6px;
    background: #fff;
    border-radius: 4px;
    font-size: 12px;
    &:hover { background: #fef3c7; }
  }
  .dealer-level-evidence-icon { font-size: 16px; flex-shrink: 0; }
  .dealer-level-evidence-name {
    flex: 1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    color: #1a1a1a;
    cursor: pointer;
    &:hover { color: #1476ff; text-decoration: underline; }
  }
  .dealer-level-evidence-remove {
    cursor: pointer;
    color: #c0c4cc;
    padding: 2px;
    &:hover { color: #f56c6c; }
  }
}
</style>

<style lang="scss">
/* 鐜颁唬椋庢牸纭寮圭獥锛堝叏灞€锛?confirm 娓叉煋鍦?body锛?*/
.modern-confirm {
  width: 420px !important;
  border-radius: 16px !important;
  overflow: hidden;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15) !important;
  
  .el-message-box__header {
    background: linear-gradient(135deg, #3b82f6, #6366f1);
    padding: 16px 20px;
    
    .el-message-box__title {
      color: #fff;
      font-weight: 600;
      font-size: 16px;
    }
    
    .el-message-box__headerbtn {
      top: 16px;
      .el-message-box__close {
        color: rgba(255, 255, 255, 0.8);
        &:hover { color: #fff; }
      }
    }
  }
  
  .el-message-box__content {
    padding: 24px 20px;
    
    .el-message-box__status {
      font-size: 22px !important;
    }
    
    .el-message-box__message p {
      font-size: 14px;
      color: #374151;
      line-height: 1.6;
    }
  }
  
  .el-message-box__btns {
    padding: 12px 20px 20px;
    
    .el-button {
      border-radius: 20px;
      padding: 9px 24px;
      font-weight: 500;
    }
    
    .el-button--primary {
      background: linear-gradient(135deg, #3b82f6, #6366f1);
      border: none;
      &:hover {
        background: linear-gradient(135deg, #2563eb, #4f46e5);
      }
    }
  }
}
</style>



